import './polyfills';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { BrowserRouter } from 'react-router';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import Fetch from 'i18next-fetch-backend';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { JxtI18n, JxtI18nextOptionsFactory } from '@jooxter/i18n';
import infos from '../package.json';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ICU from 'i18next-icu';
import { getEnv } from '@jooxter/env';
import {
  AuthProvider,
  ExceptionalOpeningHoursFormProvider,
  FloorExceptionalClosingHoursFormProvider,
  ModalLocateUserProvider,
  ModalManageBookingsProvider,
  ModalViolationsProvider,
  ModalWorkplaceProvider,
  queryClient,
} from '@jooxter/core';
import { JxtWebRouterProvider } from './routes/provider';
import { setWindowName, WindowNameEnum, TEAMS_CONTEXT_QUERY_PARAM, TEAMS_CONTEXT_QUERY_VALUE } from '@jooxter/utils';
import { StrictMode, Suspense } from 'react';
import { beforeSend, denyUrls, ignoreErrors, JxtErrorFallback } from './sentry';
import { AppLoader } from './routes/AppLoader';

// Declare this window as the webapp before anything reads it (getWindowName / CookieService).
// setWindowName only takes effect when window.name isn't already set, so a webapp loaded inside
// the Teams "App" tab frame (window.name still carrying the host's/our own TEAMS marker) keeps
// being detected as TEAMS. But that redirect is a cross-origin navigation, and some browsers
// reset window.name as an anti-tracking measure — when the TEAMS_CONTEXT_QUERY_PARAM the
// redirect appended survived instead, prefer that origin-independent signal over blindly
// declaring WEBAPP here.
const isTeamsRedirect =
  new URLSearchParams(window.location.search).get(TEAMS_CONTEXT_QUERY_PARAM) === TEAMS_CONTEXT_QUERY_VALUE;
setWindowName(isTeamsRedirect ? WindowNameEnum.TEAMS : WindowNameEnum.WEBAPP);

const { DEV, MODE, ENV_NAME } = getEnv();
let i18nEnv: 'development' | 'prod' | 'staging' = DEV ? 'development' : 'prod';

if (MODE && ['staging', 'preproduction'].includes(MODE)) {
  i18nEnv = 'staging';
}

// eslint-disable-next-line react-hooks/rules-of-hooks
JxtI18n.use(Fetch)
  .use(ICU)
  .use(initReactI18next)
  .init(JxtI18nextOptionsFactory.build('webapp', i18nEnv, { version: infos.version }));

if (ENV_NAME && ['app', 'preprod'].includes(ENV_NAME)) {
  Sentry.init({
    dsn: 'https://9a25f42af93743a6a730be6b06e0254f@o814636.ingest.sentry.io/4505092085055488',
    release: `webapp-${infos.version}`,
    environment: MODE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      // 'warn' used to be captured too and made up ~97% of the error quota (third-party SDK
      // output, loading-state warnings, expected API failures). Only real errors now.
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    // Performance Monitoring — transactions have their own quota; 10% is plenty to keep the
    // N+1 / slow-endpoint insights without paying for every page view.
    tracesSampleRate: DEV ? 1.0 : 0.1,
    // Session Replay
    replaysSessionSampleRate: DEV ? 1.0 : 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.2, // A replay per error is more than the free replay quota can take; one in five is enough to reproduce.
    ignoreErrors,
    denyUrls,
    beforeSend,
  });
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={JxtI18n}>
        {/*
          The translations load before anything can render (react-i18next suspends): the app's
          loader meanwhile, like index.html's, instead of a blank page. It must not translate.
        */}
        <Suspense fallback={<AppLoader />}>
          {/*
          Global error boundary (ND-5676): renders JxtErrorFallback instead of a blank page and
          reports the crash to Sentry (a no-op when Sentry isn't initialized). It needs i18n above
          it for the fallback's strings, and sits above the router so a crash inside it is caught
          too — which is why the fallback navigates through window.location, not useNavigate.
        */}
          <Sentry.ErrorBoundary fallback={<JxtErrorFallback />}>
            <BrowserRouter>
              <ModalViolationsProvider>
                <ModalLocateUserProvider>
                  <ExceptionalOpeningHoursFormProvider>
                    <FloorExceptionalClosingHoursFormProvider>
                      <JxtWebRouterProvider>
                        <ModalManageBookingsProvider>
                          <ModalWorkplaceProvider>
                            <AuthProvider>
                              <App />
                            </AuthProvider>
                            <ReactQueryDevtools initialIsOpen={false} />
                          </ModalWorkplaceProvider>
                        </ModalManageBookingsProvider>
                      </JxtWebRouterProvider>
                    </FloorExceptionalClosingHoursFormProvider>
                  </ExceptionalOpeningHoursFormProvider>
                </ModalLocateUserProvider>
              </ModalViolationsProvider>
            </BrowserRouter>
          </Sentry.ErrorBoundary>
        </Suspense>
      </I18nextProvider>
    </QueryClientProvider>
  </StrictMode>
);
